import { UserOverview } from "@src/domains/shared/models/Dtos/userOverview";
import { Roles } from "@src/domains/shared/models/Enums/Roles";
import { TUserRoles } from "@src/interfaces/TUserRoles";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface AuthState {
  user: UserOverview | null;
  userRoles: TUserRoles[];
  isCheckingUser: boolean;
  isAdmin: () => boolean;
  selectedOfficeId: number | null;
  setUser: (userData: UserOverview | null) => void;
  setSelectedOfficeId: (officeId: number | null) => void;
  setUserRoles: (userRoles: TUserRoles[]) => void;
  setIsCheckingUser: (value: boolean) => void;
  clearUser: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      userRoles: [],
      isCheckingUser: false,
      selectedOfficeId: null,
      isAdmin: () =>
        [
          Roles.SuperAdmin,
          Roles.ProcessAdmin,
          Roles.ProcessCustomerAdmin,
          Roles.InventoryAdmin,
        ].some((role) => get().userRoles.includes(role)),
      setUser: (userData) =>
        set((state) => {
          const offices = userData?.customerOffices ?? [];
          const hasCurrentOffice = offices.some(
            (office) => office.id === state.selectedOfficeId,
          );

          let nextSelectedOfficeId: number | null = null;
          if (hasCurrentOffice) {
            nextSelectedOfficeId = state.selectedOfficeId;
          } else if (offices.length === 1) {
            // Keep auto-select for users with a single office.
            nextSelectedOfficeId = offices[0].id;
          }

          return {
            user: userData,
            userRoles: userData?.roleNames ?? [],
            selectedOfficeId: nextSelectedOfficeId,
          };
        }),
      setSelectedOfficeId: (officeId) => set({ selectedOfficeId: officeId }),
      setUserRoles: (userRoles) => set({ userRoles: userRoles ?? [] }),
      setIsCheckingUser: (value) => set({ isCheckingUser: value }),
      clearUser: () => set({ user: null, userRoles: [], selectedOfficeId: null }),
    }),
    {
      name: "carpilot360.auth-store",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        selectedOfficeId: state.selectedOfficeId,
        user: state.user,
        userRoles: state.userRoles,
      }),
    },
  ),
);
